import {useElementSize} from '@mantine/hooks';
import {IconFile} from '@tabler/icons-react';
import type * as PdfJsModule from 'pdfjs-dist/legacy/build/pdf.mjs';
import type {PDFDocumentProxy, RenderTask} from 'pdfjs-dist/legacy/build/pdf.mjs';
import React, {useEffect, useRef, useState} from 'react';
import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {HelpText} from 'src/design/atoms/HelpText';
import {Skeleton} from 'src/design/atoms/Skeleton';
import {Stack} from 'src/design/atoms/Stack';

export interface PdfPagesProps {
  src: string;
  title?: string;
}

type PdfJs = typeof PdfJsModule;

interface PageSize {
  width: number;
  height: number;
}

const MAX_PIXEL_RATIO = 2;
// Width bucket keeps resizes from re-rasterizing every page on each pixel of change.
const WIDTH_BUCKET_PX = 200;

// Copied from pdfjs-dist by vite.config.ts; JBIG2/JPEG 2000 scans and ICC color decode via the wasm.
const PDFJS_ASSET_BASE = `${import.meta.env.BASE_URL}pdfjs/`;
const PDFJS_ASSET_URLS = {
  wasmUrl: `${PDFJS_ASSET_BASE}wasm/`,
  standardFontDataUrl: `${PDFJS_ASSET_BASE}standard_fonts/`,
  iccUrl: `${PDFJS_ASSET_BASE}iccs/`,
  cMapUrl: `${PDFJS_ASSET_BASE}cmaps/`,
  cMapPacked: true,
};

let pdfJsPromise: Promise<PdfJs> | undefined;

// Legacy build: the modern one calls Map.getOrInsertComputed unpolyfilled, which older Safari lacks.
const loadPdfJs = (): Promise<PdfJs> => {
  if (!pdfJsPromise) {
    pdfJsPromise = Promise.all([
      import('pdfjs-dist/legacy/build/pdf.mjs'),
      import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
    ]).then(([pdfjs, worker]) => {
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      return pdfjs;
    });
    pdfJsPromise.catch(() => {
      pdfJsPromise = undefined;
    });
  }
  return pdfJsPromise;
};

const isRenderCancelled = (error: unknown): boolean =>
  error instanceof Error && error.name === 'RenderingCancelledException';

const pageFrameStyle: React.CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  background: 'var(--mantine-color-white)',
  border: '1px solid var(--mantine-color-gray-3)',
  borderRadius: 'var(--mantine-radius-sm)',
  boxShadow: 'var(--mantine-shadow-xs)',
};

interface PdfPageCanvasProps {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  size: PageSize;
  targetWidth: number;
  label: string;
}

const PdfPageCanvas: React.FC<PdfPageCanvasProps> = ({
  pdf,
  pageNumber,
  size,
  targetWidth,
  label,
}) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof IntersectionObserver === 'undefined') {
      setIsNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      {rootMargin: '100% 0px'}
    );
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!isNearViewport || !canvas || targetWidth <= 0) return;

    let cancelled = false;
    let renderTask: RenderTask | undefined;

    const render = async () => {
      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;
        const pixelRatio = Math.min(
          window.devicePixelRatio || 1,
          MAX_PIXEL_RATIO
        );
        const scale = (targetWidth * pixelRatio) / size.width;
        const viewport = page.getViewport({scale});
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        renderTask = page.render({canvas, viewport});
        await renderTask.promise;
        if (!cancelled) setIsRendered(true);
      } catch (error) {
        if (!cancelled && !isRenderCancelled(error)) setIsRendered(true);
      }
    };
    void render();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, pageNumber, size.width, targetWidth, isNearViewport]);

  return (
    <Box ref={frameRef} style={pageFrameStyle}>
      {!isRendered && (
        <Skeleton
          radius={0}
          style={{position: 'absolute', inset: 0, height: '100%'}}
        />
      )}
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={label}
        width={Math.round(size.width)}
        height={Math.round(size.height)}
        style={{display: 'block', width: '100%', height: 'auto'}}
      />
    </Box>
  );
};

/**
 * Renders every page of a PDF as a plain full-width canvas, with no viewer chrome,
 * so the pages read as part of the page. Pages rasterize lazily as they near the viewport.
 */
export const PdfPages: React.FC<PdfPagesProps> = ({src, title}) => {
  const {ref: containerRef, width} = useElementSize();
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy>();
  const [pageSizes, setPageSizes] = useState<PageSize[]>([]);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let destroyLoading: (() => Promise<void>) | undefined;

    setPdfDocument(undefined);
    setPageSizes([]);
    setHasFailed(false);

    const load = async () => {
      try {
        const pdfjs = await loadPdfJs();
        if (cancelled) return;
        const loadingTask = pdfjs.getDocument({
          url: src,
          disableRange: true,
          ...PDFJS_ASSET_URLS,
        });
        destroyLoading = () => loadingTask.destroy();
        const doc = await loadingTask.promise;
        const pages = await Promise.all(
          Array.from({length: doc.numPages}, (_, index) =>
            doc.getPage(index + 1)
          )
        );
        if (cancelled) return;
        const sizes: PageSize[] = pages.map(page => {
          const viewport = page.getViewport({scale: 1});
          return {width: viewport.width, height: viewport.height};
        });
        setPageSizes(sizes);
        setPdfDocument(doc);
      } catch {
        if (!cancelled) setHasFailed(true);
      }
    };
    void load();

    return () => {
      cancelled = true;
      void destroyLoading?.().catch(() => undefined);
    };
  }, [src]);

  if (hasFailed) {
    return (
      <Stack gap="xs">
        <HelpText text="Unable to display this PDF." />
        <Anchor href={src} target="_blank" rel="noopener noreferrer" c="black">
          <Group gap="xs">
            <IconFile size={18} />
            {title ?? 'Open PDF'}
          </Group>
        </Anchor>
      </Stack>
    );
  }

  const targetWidth =
    width > 0 ? Math.ceil(width / WIDTH_BUCKET_PX) * WIDTH_BUCKET_PX : 0;
  const pageLabelPrefix = title ?? 'PDF';

  return (
    <Stack ref={containerRef} gap="sm" w="100%">
      {!pdfDocument ? (
        <Skeleton height={400} radius="sm" />
      ) : (
        pageSizes.map((size, index) => (
          <PdfPageCanvas
            key={index}
            pdf={pdfDocument}
            pageNumber={index + 1}
            size={size}
            targetWidth={targetWidth}
            label={`${pageLabelPrefix} page ${index + 1} of ${pageSizes.length}`}
          />
        ))
      )}
    </Stack>
  );
};
